import { useState, useEffect } from 'react';
import loadImage from 'blueimp-load-image/js';
import LoadingGif from '../../table/table-loader/assets/table-loader.gif';
import { uploadFileToS3 } from '@lua-frontend/utils';

import { Images } from '../../assets/index';
import '../upload-image/styles.scss'; // reuse same styles

import 'react-image-crop/dist/ReactCrop.css';
import { useImageCrop } from './useImageCrop';

/**
 * Custom hook that contains all the upload logic
 */
export const useUploadAttachments = ({
  field,
  form,
  enableCrop = false,
  allowMultiple = false,
  outputType = 'keys', // 'keys' | 'details'
  maxSize = null,
  folderPath,
  prefix,
  isPrivate = false,
  apiUrl,
  cropVariant = 'square', // new: basic variant selection
  cropPresets, // optional: custom variant map
  skiptemp = false, // new: skip temp bucket for direct upload
  useAcceleratedEndpoint = false
}) => {
  const {
    logoModal,
    setLogoModal,
    upImg,
    setUpImg,
    crop,
    setCrop,
    base64ImgRef,
    onLoad,
    makeClientCrop,
    getCroppedFile,
    setImageType
  } = useImageCrop();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [isImageLoaded, setIsImageLoaded] = useState(true);
  const [imagePreview, setImagePreview] = useState(null);
  const [pendingCropFiles, setPendingCropFiles] = useState([]);
  const [currentCropIndex, setCurrentCropIndex] = useState(0);

  useEffect(() => {
    const value = field?.value;
    const hasValue = Array.isArray(value)
      ? value.length > 0
      : value !== undefined && value !== null && value !== '';

    if (!hasValue) setImagePreview(null);
  }, [field?.value]);

  const getVariantAspect = () => {
    const variants = {
      square: 1,
      landscape: 16 / 9,
      portrait: 3 / 4,
      free: undefined,
      ...(cropPresets || {})
    };
    return variants[cropVariant];
  };

  /**
   * Helper to upload a file directly to S3 (used for non-image files or when cropping fails)
   */
  const uploadFileDirectly = async (files) => {
    const fileArray = Array.isArray(files) ? files : [files];

    if (!fileArray.length) return;

    setIsUploading(true);
    setUploadError(null);

    try {
      let apiBaseUrl = import.meta.env.VITE_APP_NODE_API_ENDPOINT;

      const options = {
        folderPath: folderPath,
        prefix: prefix,
        isPrivate: isPrivate,
        apiUrl: apiUrl || apiBaseUrl || undefined,
        skipTempBucket: skiptemp,
        useAcceleratedEndpoint: useAcceleratedEndpoint,
        onStart: () => {
          setIsUploading(true);
          if (form && form.setFieldValue) form.setFieldValue('error', '');
        },
        onComplete: () => {
          setIsUploading(false);
        },
        onError: (error) => {
          setUploadError(error);
          setIsUploading(false);
          if (form && form.setFieldValue)
            form.setFieldValue('error', error?.message || 'Upload failed');
        }
      };

      const uploaded = [];

      for (const file of fileArray) {
        const result = await uploadFileToS3(file, options);

        if (result) {
          uploaded.push({
            file,
            type: file.type,
            name: file.name,
            key: result.key,
            size: file.size
          });
        } else {
          const error = new Error('Upload failed - no result returned');
          setUploadError(error);
          if (form && form.setFieldValue) form.setFieldValue('error', error.message);
          break;
        }
      }

      if (uploaded.length) {
        const lastUploaded = uploaded[uploaded.length - 1];

        const existingValue =
          (form && form.values && form.values[field.name]) ?? (field ? field.value : undefined);

        const existingArray = Array.isArray(existingValue)
          ? existingValue
          : existingValue != null && existingValue !== ''
            ? [existingValue]
            : [];

        const existingKeys = existingArray.map((item) => item?.key || item);
        const newKeys = uploaded.map((item) => item.key);

        const allKeys = allowMultiple ? [...existingKeys, ...newKeys] : lastUploaded.key;
        const allDetails = allowMultiple ? [...existingArray, ...uploaded] : lastUploaded;

        if (form && form.setFieldValue) {
          if (outputType === 'keys') {
            form.setFieldValue(field.name, allKeys);
          } else if (outputType === 'details') {
            form.setFieldValue(field.name, allDetails);
          }
          form.setFieldValue(`${field.name}Changed`, true);
          form.setFieldValue('error', '');
        }

        setIsUploading(false);
        setImagePreview(URL.createObjectURL(lastUploaded.file));
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown upload error');
      setUploadError(error);
      setIsUploading(false);
      if (form && form.setFieldValue) form.setFieldValue('error', error.message);
    }
  };

  const getExistingTotalSize = () => {
    const existingValue =
      (form && form.values && form.values[field.name]) ?? (field ? field.value : undefined);

    const existingArray = Array.isArray(existingValue)
      ? existingValue
      : existingValue != null && existingValue !== ''
        ? [existingValue]
        : [];

    return existingArray.reduce((sum, item) => {
      if (item && typeof item === 'object' && 'size' in item) {
        return sum + (item.size || 0);
      }
      return sum;
    }, 0);
  };

  const openCropForFile = (file) => {
    if (!file) return;

    const fileType = file.type || '';

    if (form && form.setFieldValue) {
      form.setFieldValue('error', '');
    }

    const aspect = getVariantAspect();
    setCrop({
      ...crop,
      unit: '%',
      width: 200,
      height: 'unset',
      ...(typeof aspect === 'number' ? { aspect } : {})
    });

    loadImage(
      file,
      (img) => {
        if (typeof img.toDataURL !== 'function') {
          uploadFileDirectly(file);
          return;
        }
        const base64data = img.toDataURL(fileType);
        setUpImg(base64data);
        setLogoModal(true);
        setImageType(fileType);
      },
      { orientation: true, canvas: true }
    );
  };

  const onSelectFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);

      if (maxSize && typeof maxSize === 'number') {
        const newFilesTotal = files.reduce((sum, file) => sum + (file?.size || 0), 0);
        const totalSize = newFilesTotal + getExistingTotalSize();

        if (totalSize > maxSize) {
          const error = new Error('Total file size exceeds the allowed limit');
          setUploadError(error);
          if (form && form.setFieldError) {
            form.setFieldError(field.name, error.message);
          }

          // eslint-disable-next-line no-param-reassign
          e.target.value = '';
          return;
        }
      }

      const selectedFile = files[0];
      const imageFiles = files.filter((file) => (file.type || '').startsWith('image/'));
      const nonImageFiles = files.filter((file) => !(file.type || '').startsWith('image/'));

      // For direct upload mode, upload immediately to S3 (temp bucket)
      if (enableCrop === false || !imageFiles.length) {
        if (allowMultiple) {
          uploadFileDirectly(files);
        } else {
          uploadFileDirectly(selectedFile);
        }
        // Clear input so selecting the same files again still triggers onChange
        // eslint-disable-next-line no-param-reassign
        e.target.value = '';
        return;
      }

      // For crop mode, upload non-images directly and crop images one by one
      if (nonImageFiles.length) {
        uploadFileDirectly(allowMultiple ? nonImageFiles : nonImageFiles[0]);
      }

      if (enableCrop && imageFiles.length) {
        setPendingCropFiles(imageFiles);
        setCurrentCropIndex(0);
        openCropForFile(imageFiles[0]);
      }

      // Clear input after setting up crop queue so the same files can be selected again later
      // eslint-disable-next-line no-param-reassign
      e.target.value = '';
    }
  };

  const resetImage = (event) => {
    event.preventDefault();
    form.setFieldValue(field.name, '');
    form.setFieldValue(`${field.name}Changed`, true);
    setUpImg('');
    setImagePreview(null);
  };

  const handleImageLoaded = () => {
    setIsImageLoaded(false);
  };

  const handleImageErrored = (ev) => {
    ev.target.src = 'https://d1uj83pvrphe42.cloudfront.net/default/default_profile_pic.png';
    setIsImageLoaded(false);
  };

  // Determine the image source
  const imageSource = imagePreview
    ? imagePreview
    : field?.value
      ? field.value
      : 'https://d1uj83pvrphe42.cloudfront.net/default/default_profile_pic.png';

  const onCropComplete = async () => {
    try {
      if (!base64ImgRef.current) {
        return;
      }

      const file = await getCroppedFile();
      if (!file) {
        console.error('No cropped file available');
        return;
      }

      setImagePreview(URL.createObjectURL(file));
      setIsUploading(true);
      setUploadError(null);

      try {
        let apiBaseUrl = import.meta.env.VITE_APP_NODE_API_ENDPOINT;

        const options = {
          folderPath: folderPath,
          prefix: prefix,
          isPrivate: isPrivate,
          apiUrl: apiUrl || apiBaseUrl || undefined,
          skipTempBucket: skiptemp,
          useAcceleratedEndpoint: useAcceleratedEndpoint,
          onStart: () => {
            setIsUploading(true);
            if (form && form.setFieldValue) form.setFieldValue('error', '');
          },
          onComplete: () => {
            setIsUploading(false);
          },
          onError: (error) => {
            setUploadError(error);
            setIsUploading(false);
            if (form && form.setFieldValue)
              form.setFieldValue('error', error?.message || 'Upload failed');
          }
        };

        const result = await uploadFileToS3(file, options);

        if (result) {
          // Update the Formik field value with the file key
          const existingValue =
            (form && form.values && form.values[field.name]) ?? (field ? field.value : undefined);

          const existingArray = Array.isArray(existingValue)
            ? existingValue
            : existingValue != null && existingValue !== ''
              ? [existingValue]
              : [];

          const existingKeys = existingArray.map((item) => item?.key || item);

          if (form && form.setFieldValue) {
            if (allowMultiple) {
              form.setFieldValue(field.name, [...existingKeys, result.key]);
            } else {
              form.setFieldValue(field.name, result.key);
            }
            form.setFieldValue(`${field.name}Changed`, true);
            form.setFieldValue('error', '');
          }

          const hasMore =
            Array.isArray(pendingCropFiles) &&
            pendingCropFiles.length > 0 &&
            currentCropIndex < pendingCropFiles.length - 1;

          if (hasMore) {
            const nextIndex = currentCropIndex + 1;
            const nextFile = pendingCropFiles[nextIndex];
            setCurrentCropIndex(nextIndex);
            openCropForFile(nextFile);
          } else {
            setPendingCropFiles([]);
            setCurrentCropIndex(0);
            setLogoModal(false);
          }
        } else {
          setUploadError(new Error('Upload failed - no result returned'));
          if (form && form.setFieldValue)
            form.setFieldValue('error', 'Upload failed - no result returned');
        }
      } catch (uploadErr) {
        setUploadError(uploadErr instanceof Error ? uploadErr : new Error('Unknown upload error'));
        setIsUploading(false);
        if (form && form.setFieldValue)
          form.setFieldValue('error', uploadErr?.message || 'Unknown upload error');
      }
    } catch (outerErr) {
      setUploadError(outerErr instanceof Error ? outerErr : new Error('Critical error occurred'));
      setIsUploading(false);
      if (form && form.setFieldValue)
        form.setFieldValue('error', outerErr?.message || 'Critical error occurred');
    }
  };

  // Minimized, headless return
  return {
    // Common state
    isUploading,
    uploadError,
    isImageLoaded,
    imagePreview,
    imageSource,
    // Common handlers
    onSelectFile,
    resetImage,
    handleImageLoaded,
    handleImageErrored,
    // Common UI deps
    LoadingGif,
    Images,
    // Crop API only when enabled
    ...(enableCrop
      ? {
          crop: {
            isOpen: logoModal,
            src: upImg,
            rect: crop,
            open: () => setLogoModal(true),
            close: () => setLogoModal(false),
            setRect: setCrop,
            onImageLoaded: onLoad,
            onChange: setCrop,
            makePreview: makeClientCrop,
            confirm: onCropComplete,
            variant: cropVariant
          }
        }
      : {})
  };
};
